<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Diagram, Node as GoNode } from 'gojs'
import { formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { nowApiDateTime } from '@yuksalish/ui'
import { useOperatorApi } from '../composables/useOperatorApi'
import type {
  EquipmentMachine,
  MachineCurrent,
  MachineLayoutDetail,
  MachineLayoutNode,
  MachineLayoutSummary,
  OperatorEmployee,
  OperatorOrder,
  OperatorStage,
  OrderVariantOption,
} from '../types/operator'

// go.js is ~1 MB, so it is only downloaded when the floor plan page is opened.
type GoNamespace = typeof import('gojs')
let goModule: GoNamespace | null = null
async function loadGo(): Promise<GoNamespace> {
  if (!goModule) goModule = await import('gojs')
  return goModule
}

const { t } = useI18n()
const auth = useAuthStore()
const {
  listLayouts,
  getLayout,
  createLayout,
  createLayoutNode,
  updateLayoutNode,
  listEquipmentMachines,
  getMachineCurrent,
  closeMachineRun,
  listProductionOrders,
  getOrder,
  listStages,
  startMachineRun,
  listProductionEmployees,
} = useOperatorApi()

const layouts = ref<MachineLayoutSummary[]>([])
const selectedCode = ref('')
const detail = ref<MachineLayoutDetail | null>(null)
const loading = ref(true)
const error = ref('')
const message = ref('')
const saving = ref(false)
const canManage = computed(() => auth.user?.permissions.includes('production_orders.manage') ?? false)
const coordinateEditingEnabled = ref(false)

const machines = ref<EquipmentMachine[]>([])
const showNodeForm = ref(false)
const nodeForm = reactive({ machine_id: 0, x: 120, y: 120, width: 70, height: 44 })
const nodeError = ref('')
const showLayoutForm = ref(false)
const layoutForm = reactive({ code: '', name: '', width: 2400, height: 1200 })
const layoutError = ref('')

// --- machine offcanvas --------------------------------------------------
const selectedMachineNode = ref<MachineLayoutNode | null>(null)
const machineCurrent = ref<MachineCurrent | null>(null)
const machineLoading = ref(false)
const machineError = ref('')
const machineActionError = ref('')
const machineActionMessage = ref('')
const finishingRun = ref(false)
const showLoadOrder = ref(false)
const orderChoices = ref<OperatorOrder[]>([])
const orderChoicesLoading = ref(false)
const orderVariants = ref<OrderVariantOption[]>([])
const orderStages = ref<OperatorStage[]>([])
const startingRun = ref(false)
const loadOrderForm = reactive({
  order_id: 0,
  variant_id: 0,
  stage_code: '',
  planned_quantity: 0,
  target_rate_per_hour: '',
  shift: 'day',
})
const currentRun = computed(() => machineCurrent.value?.run ?? null)
const currentState = computed(() => machineCurrent.value?.state ?? null)

// --- batch order assignment ---------------------------------------------
const showBatchAssign = ref(false)
const batchLoading = ref(false)
const batchSubmitting = ref(false)
const batchError = ref('')
const batchMessage = ref('')
const batchVariants = ref<OrderVariantOption[]>([])
const batchStages = ref<OperatorStage[]>([])
const batchOperators = ref<OperatorEmployee[]>([])
const selectedBatchMachines = ref<number[]>([])
const batchAssignments = reactive<Record<number, number>>({})
const batchQuantities = reactive<Record<number, number>>({})
const batchForm = reactive({ order_id: 0, operator_employee_id: 0, stage_code: '', target_rate_per_hour: '', shift: 'day' })
const layoutMachines = computed(() =>
  (detail.value?.nodes ?? []).filter(
    (node): node is MachineLayoutNode & { machine_id: number } => node.category === 'machine' && Boolean(node.machine_id),
  ),
)

function machineUnavailable(node: MachineLayoutNode): boolean {
  return Boolean(node.run_id || node.order_id) || ['down', 'blocked', 'maintenance', 'off'].includes(node.state ?? '')
}

function clearBatchAssignments(): void {
  selectedBatchMachines.value = []
  for (const key of Object.keys(batchAssignments)) delete batchAssignments[Number(key)]
  for (const key of Object.keys(batchQuantities)) delete batchQuantities[Number(key)]
}

async function openBatchAssign(): Promise<void> {
  showBatchAssign.value = true
  batchError.value = ''
  batchMessage.value = ''
  batchVariants.value = []
  batchStages.value = []
  Object.assign(batchForm, { order_id: 0, operator_employee_id: 0, stage_code: '', target_rate_per_hour: '', shift: 'day' })
  clearBatchAssignments()
  batchLoading.value = true
  try {
    const [active, planned, employees] = await Promise.all([
      listProductionOrders({ status: 'in_progress', page: 1, per_page: 100 }),
      listProductionOrders({ status: 'planned', page: 1, per_page: 100 }),
      listProductionEmployees(),
    ])
    batchOperators.value = employees
    const seen = new Set<number>()
    orderChoices.value = [...active.items, ...planned.items].filter((order) => {
      if (seen.has(order.id)) return false
      seen.add(order.id)
      return true
    })
  } catch (cause) {
    batchError.value = formatApiError(cause)
  } finally {
    batchLoading.value = false
  }
}

async function onBatchOrderChange(): Promise<void> {
  batchError.value = ''
  batchVariants.value = []
  batchStages.value = []
  batchForm.stage_code = ''
  clearBatchAssignments()
  if (!batchForm.order_id) return
  batchLoading.value = true
  try {
    const [order, stages] = await Promise.all([getOrder(batchForm.order_id), listStages(batchForm.order_id)])
    batchVariants.value = order.variants ?? []
    batchStages.value = stages
    batchForm.stage_code = stages[0]?.stage_code ?? ''
  } catch (cause) {
    batchError.value = formatApiError(cause)
  } finally {
    batchLoading.value = false
  }
}

function toggleBatchMachine(machineId: number, selected: boolean): void {
  selectedBatchMachines.value = selected
    ? [...selectedBatchMachines.value, machineId]
    : selectedBatchMachines.value.filter((id) => id !== machineId)
  if (!selected) {
    delete batchAssignments[machineId]
    delete batchQuantities[machineId]
  }
}

function assignBatchVariant(machineId: number, variant: OrderVariantOption): void {
  batchAssignments[machineId] = variant.id
  if (!batchQuantities[machineId]) batchQuantities[machineId] = Number(variant.planned_quantity) || 1
}

async function submitBatchAssign(): Promise<void> {
  batchError.value = ''
  batchMessage.value = ''
  const machinesToAssign = selectedBatchMachines.value.filter((id) => batchAssignments[id])
  if (!batchForm.order_id || !batchForm.operator_employee_id || !batchForm.stage_code || !machinesToAssign.length) {
    batchError.value = t('operator.shopfloor.batchRequired')
    return
  }
  if (machinesToAssign.length !== selectedBatchMachines.value.length) {
    batchError.value = t('operator.shopfloor.batchVariantRequired')
    return
  }
  batchSubmitting.value = true
  const results = await Promise.allSettled(
    machinesToAssign.map((machineId) => {
      const variant = batchVariants.value.find((item) => item.id === batchAssignments[machineId])!
      return startMachineRun(machineId, {
        order_id: batchForm.order_id,
        variant_id: variant.id,
        color_name: variant.color_name,
        size_label: variant.size_label,
        stage_code: batchForm.stage_code,
        planned_quantity: Number(batchQuantities[machineId]) || variant.planned_quantity,
        target_rate_per_hour: Number(batchForm.target_rate_per_hour) || undefined,
        operator_employee_id: batchForm.operator_employee_id,
        started_at: nowApiDateTime(),
        shift: batchForm.shift,
      })
    }),
  )
  const failures = results.filter((result) => result.status === 'rejected')
  const successCount = results.length - failures.length
  if (failures.length) {
    batchError.value = `${t('operator.shopfloor.batchPartial', { success: successCount, failed: failures.length })} ${formatApiError((failures[0] as PromiseRejectedResult).reason)}`
  } else {
    batchMessage.value = t('operator.shopfloor.batchAssigned', { count: successCount })
    clearBatchAssignments()
  }
  batchSubmitting.value = false
  if (selectedCode.value) await selectLayout(selectedCode.value)
}

// --- go.js diagram -------------------------------------------------------
const canvasRef = ref<HTMLDivElement | null>(null)
const fullscreenRef = ref<HTMLDivElement | null>(null)
const isFullscreen = ref(false)
let diagram: Diagram | null = null
const stateColors: Record<string, string> = {
  running: '#dcefe7',
  idle: '#eaf0ed',
  setup: '#fef3cd',
  down: '#fde8e8',
  maintenance: '#e8eefd',
  blocked: '#fde8e8',
  off: '#e9ecea',
}

function nodeFill(state: string | null | undefined): string {
  return stateColors[state ?? ''] ?? '#eef2f1'
}

function hasActiveRun(node: MachineLayoutNode): boolean {
  return Boolean(node.run_id || node.order_id)
}

function machineNodeFill(node: MachineLayoutNode): string {
  switch (node.state) {
    case 'down':
    case 'blocked':
      return '#f8d7da'
    case 'maintenance':
      return '#dce6f8'
    case 'setup':
      return '#fff0c2'
    case 'off':
      return '#e2e5e4'
    default:
      // Active machines (open run / order) are green.
      return hasActiveRun(node) || node.state === 'running' ? '#bfeacf' : '#ecefed'
  }
}

/** Card text size derived from the node box so code / colour / size always fit. */
function cardFont(node: MachineLayoutNode, bold = false): string {
  const [width = 60, height = 40] = (node.size || '').split(/\s+/).map(Number)
  const chars = Math.max(4, (node.machine_code || node.label || '').length)
  const byHeight = Math.floor((Number.isFinite(height) ? height : 40) / 4)
  const byWidth = Math.floor((Number.isFinite(width) ? width : 60) / (chars * 0.62))
  const size = Math.max(6, Math.min(13, Math.min(byHeight, byWidth)))
  return `${bold ? 'bold ' : ''}${size}px sans-serif`
}

function nodeTooltip(node: MachineLayoutNode): string {
  return [
    node.machine_name || node.label || node.machine_code,
    node.order_code ? `${t('operator.shopfloor.tipOrder')}: ${node.order_code}` : '',
    node.product_name ? `${t('operator.shopfloor.tipProduct')}: ${node.product_name}` : '',
    node.color_name || node.size_label
      ? `${t('operator.run.variant')}: ${[node.color_name, node.size_label].filter(Boolean).join(' · ')}`
      : '',
    node.state ? `${t('operator.run.machineState')}: ${t(`operator.run.states.${node.state}`)}` : '',
    node.output_today !== null && node.output_today !== undefined
      ? `${t('operator.shopfloor.tipOutput')}: ${Number(node.output_today).toLocaleString()}`
      : '',
  ]
    .filter(Boolean)
    .join('\n')
}

function variantLabel(node: MachineLayoutNode): string {
  return [node.color_name, node.size_label].filter(Boolean).join(' · ')
}

function zoomBy(factor: number): void {
  if (!diagram) return
  diagram.scale = Math.min(4, Math.max(0.2, diagram.scale * factor))
}

function zoomToFit(): void {
  diagram?.zoomToFit()
}

function handleFullscreenChange(): void {
  isFullscreen.value = document.fullscreenElement === fullscreenRef.value
  window.setTimeout(() => diagram?.requestUpdate(), 0)
}

async function enterFullscreen(): Promise<void> {
  if (!fullscreenRef.value || document.fullscreenElement) return
  await fullscreenRef.value.requestFullscreen()
}

function setCoordinateEditing(enabled: boolean): void {
  coordinateEditingEnabled.value = enabled
  if (diagram) diagram.allowMove = canManage.value && enabled
}

async function openMachinePanel(node: MachineLayoutNode): Promise<void> {
  if (!node.machine_id) return
  selectedMachineNode.value = node
  machineCurrent.value = null
  machineError.value = ''
  machineActionError.value = ''
  machineActionMessage.value = ''
  showLoadOrder.value = false
  machineLoading.value = true
  try {
    machineCurrent.value = await getMachineCurrent(node.machine_id)
  } catch (cause) {
    machineError.value = formatApiError(cause)
  } finally {
    machineLoading.value = false
  }
}

function closeMachinePanel(): void {
  selectedMachineNode.value = null
  machineCurrent.value = null
  showLoadOrder.value = false
}

async function finishCurrentRun(): Promise<void> {
  const machineId = selectedMachineNode.value?.machine_id
  const run = currentRun.value
  if (!machineId || !run) return
  machineActionError.value = ''
  machineActionMessage.value = ''
  finishingRun.value = true
  try {
    await closeMachineRun(machineId, run.id, {
      ended_at: nowApiDateTime(),
      close_reason: 'completed',
      closing_counter_total: machineCurrent.value?.counter?.counter_total ?? undefined,
    })
    machineCurrent.value = await getMachineCurrent(machineId)
    machineActionMessage.value = t('operator.run.closed')
    if (selectedCode.value) await selectLayout(selectedCode.value)
  } catch (cause) {
    machineActionError.value = formatApiError(cause)
  } finally {
    finishingRun.value = false
  }
}

async function openLoadOrderForm(): Promise<void> {
  if (currentRun.value) return
  machineActionError.value = ''
  machineActionMessage.value = ''
  showLoadOrder.value = true
  Object.assign(loadOrderForm, { order_id: 0, variant_id: 0, stage_code: '', planned_quantity: 0, target_rate_per_hour: '', shift: 'day' })
  orderVariants.value = []
  orderStages.value = []
  orderChoicesLoading.value = true
  try {
    const [active, planned] = await Promise.all([
      listProductionOrders({ status: 'in_progress', page: 1, per_page: 100 }),
      listProductionOrders({ status: 'planned', page: 1, per_page: 100 }),
    ])
    const seen = new Set<number>()
    orderChoices.value = [...active.items, ...planned.items].filter((order) => {
      if (seen.has(order.id)) return false
      seen.add(order.id)
      return true
    })
    loadOrderForm.order_id = orderChoices.value[0]?.id ?? 0
    if (loadOrderForm.order_id) await onOrderChange()
  } catch (cause) {
    machineActionError.value = formatApiError(cause)
  } finally {
    orderChoicesLoading.value = false
  }
}

async function onOrderChange(): Promise<void> {
  loadOrderForm.variant_id = 0
  loadOrderForm.stage_code = ''
  const orderId = Number(loadOrderForm.order_id)
  if (!orderId) return
  try {
    const [order, stages] = await Promise.all([getOrder(orderId), listStages(orderId)])
    orderVariants.value = order.variants ?? []
    orderStages.value = stages
    loadOrderForm.stage_code = stages[0]?.stage_code ?? ''
  } catch (cause) {
    machineActionError.value = formatApiError(cause)
  }
}

function onVariantChange(): void {
  const variant = orderVariants.value.find((item) => item.id === Number(loadOrderForm.variant_id))
  loadOrderForm.planned_quantity = Number(variant?.planned_quantity ?? 0)
}

async function submitLoadOrder(): Promise<void> {
  const machineId = selectedMachineNode.value?.machine_id
  machineActionError.value = ''
  if (!machineId || !loadOrderForm.order_id) {
    machineActionError.value = t('operator.run.orderRequired')
    return
  }
  if (!loadOrderForm.variant_id) {
    machineActionError.value = t('operator.run.variantRequired')
    return
  }
  if (!loadOrderForm.stage_code) {
    machineActionError.value = t('operator.run.stageRequired')
    return
  }
  startingRun.value = true
  try {
    await startMachineRun(machineId, {
      order_id: Number(loadOrderForm.order_id),
      variant_id: Number(loadOrderForm.variant_id),
      stage_code: loadOrderForm.stage_code,
      planned_quantity: Number(loadOrderForm.planned_quantity) || undefined,
      target_rate_per_hour: Number(loadOrderForm.target_rate_per_hour) || undefined,
      started_at: nowApiDateTime(),
      opening_counter_total: machineCurrent.value?.counter?.counter_total ?? undefined,
      shift: loadOrderForm.shift,
    })
    machineCurrent.value = await getMachineCurrent(machineId)
    showLoadOrder.value = false
    machineActionMessage.value = t('operator.run.started')
    if (selectedCode.value) await selectLayout(selectedCode.value)
  } catch (cause) {
    machineActionError.value = formatApiError(cause)
  } finally {
    startingRun.value = false
  }
}

function buildDiagram(div: HTMLDivElement, go: GoNamespace): void {
  // The layout nodes arrive already carrying the active run facts (order code,
  // model/colour/size, today's output), so the card shows what is being made.
  const cardText = (bold = false, muted = false) =>
    new go.TextBlock({
      stroke: muted ? '#2f4a40' : '#12261f',
      maxLines: 1,
      overflow: go.TextBlock.OverflowEllipsis,
      wrap: go.Wrap.None,
    }).bind('font', '', (node: MachineLayoutNode) => cardFont(node, bold))

  const machineTemplate = new go.Node(go.Panel.Auto, {
    locationSpot: go.Spot.Center,
    cursor: 'pointer',
    selectionAdorned: false,
  })
    .bindTwoWay('location', 'loc', go.Point.parse, go.Point.stringify)
    .bindTwoWay('desiredSize', 'size', go.Size.parse, go.Size.stringify)
    .bindTwoWay('angle')
    .bind('zOrder')
    .add(
      new go.Shape('RoundedRectangle', { strokeWidth: 1.5, parameter1: 8 })
        .bind('fill', '', (node: MachineLayoutNode) => machineNodeFill(node))
        .bind('stroke', '', (node: MachineLayoutNode) => (hasActiveRun(node) ? '#2f7d55' : '#5a6e66')),
    )
    .add(
      new go.Panel('Vertical', { margin: 4, defaultAlignment: go.Spot.Center })
        .add(
          cardText(true).bind(
            'text',
            '',
            (node: MachineLayoutNode) => node.machine_code || '',
          ),
        )
        .add(
          cardText()
            .bind('text', '', (node: MachineLayoutNode) => node.color_name || '')
            .bind('visible', '', (node: MachineLayoutNode) => hasActiveRun(node) && Boolean(node.color_name)),
        )
        .add(
          cardText(false, true)
            .bind('text', '', (node: MachineLayoutNode) => node.size_label || '')
            .bind('visible', '', (node: MachineLayoutNode) => hasActiveRun(node) && Boolean(node.size_label)),
        ),
    )
  // GoJS 4 constructors only take (type, init) — children are added with add().
  const tooltip = new go.Adornment('Auto')
  tooltip.add(new go.Shape({ fill: '#1a2b26', stroke: null }))
  tooltip.add(
    new go.TextBlock({ margin: 8, stroke: '#ffffff', font: '12px sans-serif' })
      .bind('text', '', (node: MachineLayoutNode) => nodeTooltip(node)),
  )
  machineTemplate.toolTip = tooltip

  const zoneTemplate = new go.Node(go.Panel.Auto, { locationSpot: go.Spot.Center })
    .bindTwoWay('location', 'loc', go.Point.parse, go.Point.stringify)
    .bindTwoWay('desiredSize', 'size', go.Size.parse, go.Size.stringify)
    .bindTwoWay('angle')
    .bind('zOrder')
    .add(new go.Shape('Rectangle', { fill: '#102635', stroke: '#315064', strokeWidth: 1 }))
    .add(
      new go.TextBlock({
        margin: 6,
        font: '11px sans-serif',
        stroke: '#a9c3d2',
        wrap: go.Wrap.Fit,
      }).bind('text', '', (node: MachineLayoutNode) => node.label || ''),
    )

  diagram = new go.Diagram(div, {
    allowMove: canManage.value && coordinateEditingEnabled.value,
    allowCopy: false,
    allowDelete: false,
    'undoManager.isEnabled': false,
    'toolManager.mouseWheelBehavior': go.WheelMode.Zoom,
    initialAutoScale: go.Diagram.Uniform,
    minScale: 0.2,
    maxScale: 4,
    padding: 12,
  })
  diagram.nodeTemplateMap.add('machine', machineTemplate)
  diagram.nodeTemplateMap.add('zone', zoneTemplate)
  diagram.nodeTemplateMap.add('label', zoneTemplate)
  diagram.nodeTemplateMap.add('point', machineTemplate)
  diagram.nodeTemplateMap.add('conveyor', zoneTemplate)
  const linkTemplate = new go.Link({ routing: go.Link.Orthogonal, corner: 8 })
  linkTemplate.add(new go.Shape({ stroke: '#527287', strokeWidth: 2 }))
  diagram.linkTemplate = linkTemplate

  // Keep the operator on the map and show live machine/run information.
  diagram.addDiagramListener('ObjectSingleClicked', (event) => {
    if (coordinateEditingEnabled.value) return
    const part = event.subject.part
    if (!(part instanceof go.Node) || part.category !== 'machine') return
    const machineId = (part.data as MachineLayoutNode).machine_id
    if (machineId) void openMachinePanel(part.data as MachineLayoutNode)
  })

  // Dragging a card saves its new coordinates to the layout.
  diagram.addDiagramListener('SelectionMoved', () => {
    if (!canManage.value || !coordinateEditingEnabled.value || !detail.value || !diagram) return
    const movedNodes: GoNode[] = []
    diagram.selection.each((part) => {
      if (part instanceof go.Node) movedNodes.push(part)
    })
    if (movedNodes.length) void persistNodePositions(movedNodes)
  })
}

async function persistNodePosition(node: GoNode): Promise<void> {
  if (!detail.value) return
  const data = node.data as MachineLayoutNode
  if (!data?.id) return
  const bounds = node.actualBounds
  const x = Math.round(node.location.x)
  const y = Math.round(node.location.y)
  await updateLayoutNode(detail.value.layout.code, data.id, {
    node_type: data.category,
    node_key: data.key,
    machine_id: data.machine_id ?? undefined,
    x,
    y,
    width: Math.round(bounds.width),
    height: Math.round(bounds.height),
    rotation: Math.round(node.angle),
    z_index: data.zOrder,
  })

  // Keep the model synchronized with the integer coordinates accepted by the
  // API so a later edit starts from exactly the persisted location.
  if (diagram) {
    diagram.model.setDataProperty(data, 'loc', `${x} ${y}`)
  }
}

async function persistNodePositions(nodes: GoNode[]): Promise<void> {
  message.value = ''
  try {
    await Promise.all(nodes.map((node) => persistNodePosition(node)))
    message.value = t('operator.shopfloor.positionSaved')
  } catch (cause) {
    message.value = formatApiError(cause)
    // Restore the authoritative server coordinates when any save fails instead
    // of leaving a card visually moved but unsaved.
    if (selectedCode.value) await selectLayout(selectedCode.value)
  }
}

async function renderLayout(data: MachineLayoutDetail): Promise<void> {
  await nextTick()
  if (!canvasRef.value) return
  const go = await loadGo()
  if (!diagram) buildDiagram(canvasRef.value, go)
  const nodes = data.nodes.map((node) => ({ ...node, tooltip: nodeTooltip(node) }))
  diagram!.model = new go.GraphLinksModel(nodes, data.links ?? [])
  diagram!.allowMove = canManage.value && coordinateEditingEnabled.value
}

async function selectLayout(code: string): Promise<void> {
  selectedCode.value = code
  error.value = ''
  try {
    detail.value = await getLayout(code)
    await renderLayout(detail.value)
  } catch (cause) {
    detail.value = null
    error.value = formatApiError(cause)
  }
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    layouts.value = await listLayouts()
    if (!layouts.value.length) {
      detail.value = null
      return
    }
    const code = layouts.value.some((layout) => layout.code === selectedCode.value)
      ? selectedCode.value
      : layouts.value[0].code

    // The loading branch replaces the entire canvas in the template. Remove it
    // before selecting the first layout so renderLayout() can wait for and bind
    // to the real canvas element.
    loading.value = false
    await selectLayout(code)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function refresh(): Promise<void> {
  message.value = ''
  if (!selectedCode.value) {
    await load()
    return
  }
  await selectLayout(selectedCode.value)
}

function openNodeForm(): void {
  nodeError.value = ''
  nodeForm.machine_id = machines.value[0]?.id ?? 0
  nodeForm.x = 120
  nodeForm.y = 120
  nodeForm.width = 70
  nodeForm.height = 44
  showNodeForm.value = true
  if (!machines.value.length) {
    void listEquipmentMachines()
      .then((rows) => {
        machines.value = rows.filter((machine) => machine.is_active)
        nodeForm.machine_id = machines.value[0]?.id ?? 0
      })
      .catch(() => {
        machines.value = []
      })
  }
}

async function submitNode(): Promise<void> {
  nodeError.value = ''
  if (!detail.value) return
  const machine = machines.value.find((item) => item.id === Number(nodeForm.machine_id))
  if (!machine) {
    nodeError.value = t('operator.shopfloor.machineRequired')
    return
  }
  saving.value = true
  try {
    await createLayoutNode(detail.value.layout.code, {
      node_type: 'machine',
      node_key: `m-${machine.id}`,
      machine_id: machine.id,
      x: Number(nodeForm.x) || 0,
      y: Number(nodeForm.y) || 0,
      width: Number(nodeForm.width) || 70,
      height: Number(nodeForm.height) || 44,
    })
    showNodeForm.value = false
    message.value = t('operator.shopfloor.machineAdded')
    await selectLayout(detail.value.layout.code)
  } catch (cause) {
    nodeError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

function openLayoutForm(): void {
  layoutError.value = ''
  layoutForm.code = ''
  layoutForm.name = ''
  layoutForm.width = 2400
  layoutForm.height = 1200
  showLayoutForm.value = true
}

async function submitLayout(): Promise<void> {
  layoutError.value = ''
  if (!layoutForm.code.trim() || !layoutForm.name.trim()) {
    layoutError.value = t('operator.shopfloor.layoutRequired')
    return
  }
  saving.value = true
  try {
    const created = await createLayout({
      code: layoutForm.code.trim(),
      name: layoutForm.name.trim(),
      width: Number(layoutForm.width) || 2400,
      height: Number(layoutForm.height) || 1200,
      unit: 'px',
    })
    showLayoutForm.value = false
    message.value = t('operator.shopfloor.layoutCreated')
    layouts.value = [...layouts.value, created]
    await selectLayout(created.code)
  } catch (cause) {
    layoutError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  void load()
})
onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  if (diagram) {
    diagram.div = null
    diagram = null
  }
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('operator.shopfloor.eyebrow') }}</p>
      <h2>{{ t('operator.shopfloor.title') }}</h2>
      <p>{{ t('operator.shopfloor.description') }}</p>
    </div>
    <div v-if="canManage" class="heading-actions">
      <button class="btn-secondary" type="button" @click="openLayoutForm">{{ t('operator.shopfloor.newLayout') }}</button>
      <button class="btn-primary" type="button" :disabled="!detail" @click="openNodeForm">{{ t('operator.shopfloor.addMachine') }}</button>
      <RouterLink v-if="detail" class="btn-primary" to="/shopfloor/batch-assign">{{ t('operator.shopfloor.batchAssign') }}</RouterLink>
    </div>
  </div>

  <p v-if="message" class="action-message" role="status">{{ message }}</p>

  <div class="toolbar">
    <select
      v-if="layouts.length"
      class="filter-select"
      :value="selectedCode"
      @change="selectLayout(($event.target as HTMLSelectElement).value)"
    >
      <option v-for="layout in layouts" :key="layout.code" :value="layout.code">{{ layout.name }}</option>
    </select>
    <span v-if="detail" class="shopfloor-meta">
      {{ detail.layout.area || '—' }} · {{ detail.layout.width }} × {{ detail.layout.height }} {{ detail.layout.unit || 'px' }}
      · {{ t('operator.shopfloor.nodeCount', { count: detail.nodes.length }) }}
    </span>
    <button v-if="detail" class="btn-secondary refresh-btn" type="button" :disabled="loading" @click="refresh">
      {{ t('operator.shopfloor.refresh') }}
    </button>
    <label v-if="detail && canManage" class="edit-toggle">
      <input
        type="checkbox"
        :checked="coordinateEditingEnabled"
        @change="setCoordinateEditing(($event.target as HTMLInputElement).checked)"
      />
      <span class="edit-toggle-track" aria-hidden="true"><span class="edit-toggle-thumb"></span></span>
      <span>{{ t('operator.shopfloor.editCoordinates') }}</span>
    </label>
    <button v-if="detail" class="btn-secondary fullscreen-btn" type="button" @click="enterFullscreen">
      <span aria-hidden="true">⛶</span> {{ t('operator.shopfloor.fullscreen') }}
    </button>
    <div v-if="detail" class="zoom-controls" :aria-label="t('operator.shopfloor.zoomControls')">
      <button class="zoom-btn" type="button" :title="t('operator.shopfloor.zoomOut')" @click="zoomBy(0.8)">−</button>
      <button class="zoom-btn zoom-fit" type="button" :title="t('operator.shopfloor.zoomFit')" @click="zoomToFit">{{ t('operator.shopfloor.zoomFit') }}</button>
      <button class="zoom-btn" type="button" :title="t('operator.shopfloor.zoomIn')" @click="zoomBy(1.25)">+</button>
    </div>
  </div>

  <div v-if="loading" class="state-message">{{ t('operator.state.loading') }}</div>
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" type="button" @click="load">{{ t('operator.state.retry') }}</button>
  </div>
  <div v-else-if="!layouts.length" class="state-message">
    <p>{{ t('operator.shopfloor.noLayouts') }}</p>
    <button v-if="canManage" class="btn-primary" type="button" @click="openLayoutForm">{{ t('operator.shopfloor.newLayout') }}</button>
  </div>
  <template v-else-if="detail">
    <p v-if="!canManage || coordinateEditingEnabled" class="drag-hint">
      {{ t('operator.shopfloor.clickHint') }}
      <template v-if="canManage"> · {{ t('operator.shopfloor.dragHint') }}</template>
    </p>
    <div ref="fullscreenRef" class="canvas-fullscreen" :class="{ 'is-fullscreen': isFullscreen }">
      <div ref="canvasRef" class="floor-canvas" role="application" :aria-label="t('operator.shopfloor.canvasAria')"></div>
    </div>
  </template>

  <div v-if="selectedMachineNode" class="offcanvas-backdrop" @click="closeMachinePanel"></div>
  <aside v-if="selectedMachineNode" class="offcanvas" :aria-label="t('operator.shopfloor.machineDetails')">
    <header class="offcanvas-head">
      <div>
        <p class="eyebrow">{{ t('operator.shopfloor.machineDetails') }}</p>
        <h3>{{ selectedMachineNode.machine_code || selectedMachineNode.label }}</h3>
        <p>{{ selectedMachineNode.machine_name }}</p>
      </div>
      <button class="close-btn" type="button" :aria-label="t('operator.shopfloor.closePanel')" @click="closeMachinePanel">×</button>
    </header>

    <div class="offcanvas-body">
      <p v-if="machineLoading" class="panel-state">{{ t('operator.state.loading') }}</p>
      <div v-else-if="machineError" class="panel-state error">{{ machineError }}</div>
      <template v-else-if="machineCurrent">
        <p v-if="machineActionMessage" class="action-message">{{ machineActionMessage }}</p>
        <p v-if="machineActionError" class="panel-error">{{ machineActionError }}</p>

        <section class="detail-section">
          <h4>{{ t('operator.run.machineState') }}</h4>
          <div class="state-line">
            <span class="state-dot" :style="{ background: nodeFill(currentState?.state) }"></span>
            <strong>{{ currentState ? t(`operator.run.states.${currentState.state}`) : '—' }}</strong>
          </div>
          <dl class="detail-grid">
            <div><dt>{{ t('operator.machines.machine') }}</dt><dd>{{ machineCurrent.machine.code }} · {{ machineCurrent.machine.name }}</dd></div>
            <div><dt>{{ t('operator.run.counter') }}</dt><dd>{{ machineCurrent.counter?.counter_shift?.toLocaleString() ?? '—' }}</dd></div>
          </dl>
        </section>

        <section class="detail-section">
          <h4>{{ t('operator.shopfloor.currentRun') }}</h4>
          <p v-if="!currentRun" class="empty-inline">{{ t('operator.run.noRun') }}</p>
          <dl v-else class="detail-grid">
            <div><dt>{{ t('operator.run.runCode') }}</dt><dd>{{ currentRun.run_code }}</dd></div>
            <div><dt>{{ t('operator.run.order') }}</dt><dd>{{ selectedMachineNode.order_code || `#${currentRun.production_order_id}` }}</dd></div>
            <div><dt>{{ t('operator.run.variant') }}</dt><dd>{{ [currentRun.color_name, currentRun.size_label].filter(Boolean).join(' · ') || '—' }}</dd></div>
            <div><dt>{{ t('operator.run.stage') }}</dt><dd>{{ currentRun.stage_code }}</dd></div>
            <div><dt>{{ t('operator.run.planned') }}</dt><dd>{{ Number(currentRun.planned_quantity || 0).toLocaleString() }}</dd></div>
            <div><dt>{{ t('operator.run.produced') }}</dt><dd>{{ Number(currentRun.produced_quantity || 0).toLocaleString() }}</dd></div>
            <div><dt>{{ t('operator.run.accepted') }}</dt><dd>{{ Number(currentRun.accepted_quantity || 0).toLocaleString() }}</dd></div>
            <div><dt>{{ t('operator.run.rejected') }}</dt><dd>{{ Number(currentRun.rejected_quantity || 0).toLocaleString() }}</dd></div>
          </dl>
        </section>

        <div v-if="canManage" class="panel-actions">
          <button v-if="currentRun" class="btn-danger" type="button" :disabled="finishingRun" @click="finishCurrentRun">
            {{ finishingRun ? t('operator.run.submitting') : t('operator.shopfloor.finishRun') }}
          </button>
          <button class="btn-primary" type="button" :disabled="Boolean(currentRun) || orderChoicesLoading" @click="openLoadOrderForm">
            {{ t('operator.shopfloor.loadOrder') }}
          </button>
        </div>
        <p v-if="canManage && currentRun" class="panel-hint">{{ t('operator.shopfloor.finishBeforeLoad') }}</p>

        <form v-if="showLoadOrder" class="load-order-form" @submit.prevent="submitLoadOrder">
          <h4>{{ t('operator.shopfloor.loadOrder') }}</h4>
          <label>{{ t('operator.run.order') }}
            <select v-model.number="loadOrderForm.order_id" :disabled="orderChoicesLoading" @change="onOrderChange">
              <option :value="0" disabled>{{ orderChoicesLoading ? t('operator.run.loadingOrders') : t('operator.run.selectOrder') }}</option>
              <option v-for="order in orderChoices" :key="order.id" :value="order.id">{{ order.mo_code }} · {{ order.product_name }}</option>
            </select>
          </label>
          <label>{{ t('operator.run.variant') }}
            <select v-model.number="loadOrderForm.variant_id" @change="onVariantChange">
              <option :value="0" disabled>{{ t('operator.run.selectVariant') }}</option>
              <option v-for="variant in orderVariants" :key="variant.id" :value="variant.id">{{ variant.color_name }} · {{ variant.size_label }}</option>
            </select>
          </label>
          <label>{{ t('operator.run.stage') }}
            <select v-model="loadOrderForm.stage_code">
              <option value="" disabled>{{ t('operator.run.selectStage') }}</option>
              <option v-for="stage in orderStages" :key="stage.stage_code" :value="stage.stage_code">{{ stage.stage_name }}</option>
            </select>
          </label>
          <div class="grid-2">
            <label>{{ t('operator.run.plannedQuantity') }}<input v-model.number="loadOrderForm.planned_quantity" type="number" min="1" /></label>
            <label>{{ t('operator.run.targetRate') }}<input v-model="loadOrderForm.target_rate_per_hour" type="number" min="0" /></label>
          </div>
          <label>{{ t('operator.run.shift') }}
            <select v-model="loadOrderForm.shift"><option value="day">{{ t('operator.run.day') }}</option><option value="night">{{ t('operator.run.night') }}</option></select>
          </label>
          <div class="modal-actions">
            <button class="btn-secondary" type="button" @click="showLoadOrder = false">{{ t('operator.shopfloor.cancel') }}</button>
            <button class="btn-primary" type="submit" :disabled="startingRun">{{ startingRun ? t('operator.run.submitting') : t('operator.run.startRun') }}</button>
          </div>
        </form>
      </template>
    </div>
  </aside>

  <div v-if="showBatchAssign" class="offcanvas-backdrop" @click="showBatchAssign = false"></div>
  <aside v-if="showBatchAssign" class="offcanvas batch-offcanvas" :aria-label="t('operator.shopfloor.batchAssign')">
    <header class="offcanvas-head">
      <div>
        <p class="eyebrow">{{ t('operator.shopfloor.batchEyebrow') }}</p>
        <h3>{{ t('operator.shopfloor.batchAssign') }}</h3>
      </div>
      <button class="close-btn" type="button" :aria-label="t('operator.shopfloor.closePanel')" @click="showBatchAssign = false">×</button>
    </header>
    <form class="offcanvas-body batch-form" @submit.prevent="submitBatchAssign">
      <p v-if="batchMessage" class="action-message">{{ batchMessage }}</p>
      <p v-if="batchError" class="panel-error">{{ batchError }}</p>

      <section class="batch-section">
        <h4>1. {{ t('operator.run.order') }}</h4>
        <label>{{ t('operator.run.order') }}
          <select v-model.number="batchForm.order_id" :disabled="batchLoading" @change="onBatchOrderChange">
            <option :value="0" disabled>{{ batchLoading ? t('operator.run.loadingOrders') : t('operator.run.selectOrder') }}</option>
            <option v-for="order in orderChoices" :key="order.id" :value="order.id">{{ order.mo_code }} · {{ order.product_name }}</option>
          </select>
        </label>
        <label>{{ t('operator.shopfloor.operatorWorker') }}
          <select v-model.number="batchForm.operator_employee_id" :disabled="batchLoading" required>
            <option :value="0" disabled>{{ t('operator.shopfloor.selectOperator') }}</option>
            <option v-for="employee in batchOperators" :key="employee.id" :value="employee.id">
              {{ employee.employee_code }} · {{ employee.first_name }} {{ employee.last_name }}
            </option>
          </select>
        </label>
        <div class="grid-2">
          <label>{{ t('operator.run.stage') }}
            <select v-model="batchForm.stage_code" :disabled="!batchStages.length">
              <option value="" disabled>{{ t('operator.run.selectStage') }}</option>
              <option v-for="stage in batchStages" :key="stage.stage_code" :value="stage.stage_code">{{ stage.stage_name }}</option>
            </select>
          </label>
          <label>{{ t('operator.run.shift') }}
            <select v-model="batchForm.shift"><option value="day">{{ t('operator.run.day') }}</option><option value="night">{{ t('operator.run.night') }}</option></select>
          </label>
        </div>
        <label>{{ t('operator.run.targetRate') }}
          <input v-model="batchForm.target_rate_per_hour" type="number" min="0" />
        </label>
      </section>

      <section class="batch-section">
        <div class="batch-section-head">
          <h4>2. {{ t('operator.shopfloor.selectMachines') }}</h4>
          <span>{{ t('operator.shopfloor.selectedCount', { count: selectedBatchMachines.length }) }}</span>
        </div>
        <div class="machine-choice-grid">
          <label
            v-for="node in layoutMachines"
            :key="node.machine_id"
            class="machine-choice"
            :class="{ selected: selectedBatchMachines.includes(node.machine_id), unavailable: machineUnavailable(node) }"
          >
            <input
              type="checkbox"
              :checked="selectedBatchMachines.includes(node.machine_id)"
              :disabled="machineUnavailable(node)"
              @change="toggleBatchMachine(node.machine_id, ($event.target as HTMLInputElement).checked)"
            />
            <strong>{{ node.machine_code }}</strong>
            <small>{{ node.state || 'idle' }}</small>
          </label>
        </div>
      </section>

      <section v-if="selectedBatchMachines.length && batchVariants.length" class="batch-section">
        <h4>3. {{ t('operator.shopfloor.variantMatrix') }}</h4>
        <div class="matrix-scroll">
          <table class="assignment-matrix">
            <thead><tr><th>{{ t('operator.shopfloor.machine') }}</th><th v-for="variant in batchVariants" :key="variant.id">{{ variant.color_name }}<br /><small>{{ variant.size_label }}</small></th><th>{{ t('operator.run.plannedQuantity') }}</th></tr></thead>
            <tbody>
              <tr v-for="machineId in selectedBatchMachines" :key="machineId">
                <th>{{ layoutMachines.find((node) => node.machine_id === machineId)?.machine_code }}</th>
                <td v-for="variant in batchVariants" :key="variant.id">
                  <label class="matrix-radio" :class="{ selected: batchAssignments[machineId] === variant.id }">
                    <input
                      type="radio"
                      :name="`machine-variant-${machineId}`"
                      :checked="batchAssignments[machineId] === variant.id"
                      @change="assignBatchVariant(machineId, variant)"
                    />
                    <span>✓</span>
                  </label>
                </td>
                <td><input v-model.number="batchQuantities[machineId]" class="matrix-quantity" type="number" min="1" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div class="batch-actions">
        <button class="btn-secondary" type="button" @click="showBatchAssign = false">{{ t('operator.shopfloor.cancel') }}</button>
        <button class="btn-primary" type="submit" :disabled="batchSubmitting || batchLoading || !selectedBatchMachines.length">
          {{ batchSubmitting ? t('operator.run.submitting') : t('operator.shopfloor.assignSelected') }}
        </button>
      </div>
    </form>
  </aside>

  <!-- Add machine node modal -->
  <div v-if="showNodeForm" class="modal-overlay" @click.self="showNodeForm = false">
    <form class="modal" @submit.prevent="submitNode">
      <h3>{{ t('operator.shopfloor.addMachine') }}</h3>
      <label>{{ t('operator.shopfloor.machine') }}
        <select v-model.number="nodeForm.machine_id" required>
          <option :value="0" disabled>{{ t('operator.shopfloor.selectMachine') }}</option>
          <option v-for="machine in machines" :key="machine.id" :value="machine.id">
            {{ machine.code }} · {{ machine.name }}
          </option>
        </select>
      </label>
      <div class="grid-2">
        <label>{{ t('operator.shopfloor.x') }}<input v-model.number="nodeForm.x" type="number" min="0" /></label>
        <label>{{ t('operator.shopfloor.y') }}<input v-model.number="nodeForm.y" type="number" min="0" /></label>
        <label>{{ t('operator.shopfloor.width') }}<input v-model.number="nodeForm.width" type="number" min="10" /></label>
        <label>{{ t('operator.shopfloor.height') }}<input v-model.number="nodeForm.height" type="number" min="10" /></label>
      </div>
      <small v-if="nodeError" class="error">{{ nodeError }}</small>
      <div class="modal-actions">
        <button type="button" class="btn-secondary" @click="showNodeForm = false">{{ t('operator.shopfloor.cancel') }}</button>
        <button type="submit" class="btn-primary" :disabled="saving">
          {{ saving ? t('operator.shopfloor.saving') : t('operator.shopfloor.save') }}
        </button>
      </div>
    </form>
  </div>

  <!-- New layout modal -->
  <div v-if="showLayoutForm" class="modal-overlay" @click.self="showLayoutForm = false">
    <form class="modal" @submit.prevent="submitLayout">
      <h3>{{ t('operator.shopfloor.newLayout') }}</h3>
      <label>{{ t('operator.shopfloor.layoutCode') }}<input v-model="layoutForm.code" type="text" required /></label>
      <label>{{ t('operator.shopfloor.layoutName') }}<input v-model="layoutForm.name" type="text" required /></label>
      <div class="grid-2">
        <label>{{ t('operator.shopfloor.width') }}<input v-model.number="layoutForm.width" type="number" min="100" /></label>
        <label>{{ t('operator.shopfloor.height') }}<input v-model.number="layoutForm.height" type="number" min="100" /></label>
      </div>
      <small v-if="layoutError" class="error">{{ layoutError }}</small>
      <div class="modal-actions">
        <button type="button" class="btn-secondary" @click="showLayoutForm = false">{{ t('operator.shopfloor.cancel') }}</button>
        <button type="submit" class="btn-primary" :disabled="saving">
          {{ saving ? t('operator.shopfloor.saving') : t('operator.shopfloor.save') }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.page-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.eyebrow { margin: 0 0 4px; color: #65736e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.heading-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.filter-select { padding: 8px 12px; border: 1px solid #d4d8d5; border-radius: 8px; background: #fff; font-size: 13px; min-width: 180px; }
.shopfloor-meta { color: #65736e; font-size: 12px; }
.refresh-btn { padding: 6px 12px; font-size: 12px; }
.edit-toggle { display: inline-flex; align-items: center; gap: 7px; color: #3d4d47; font-size: 12px; font-weight: 700; cursor: pointer; user-select: none; }
.edit-toggle input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.edit-toggle-track { position: relative; width: 36px; height: 20px; border-radius: 999px; background: #cbd3d0; transition: background 160ms ease; }
.edit-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgb(0 0 0 / 25%); transition: transform 160ms ease; }
.edit-toggle input:checked + .edit-toggle-track { background: #16869a; }
.edit-toggle input:checked + .edit-toggle-track .edit-toggle-thumb { transform: translateX(16px); }
.edit-toggle input:focus-visible + .edit-toggle-track { outline: 3px solid rgb(22 134 154 / 25%); outline-offset: 2px; }
.fullscreen-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; font-size: 12px; }
.fullscreen-btn span { font-size: 17px; line-height: 1; }
.zoom-controls { display: inline-flex; overflow: hidden; margin-left: auto; border: 1px solid #cbd7d2; border-radius: 8px; background: #fff; }
.zoom-btn { min-width: 34px; padding: 6px 9px; border: 0; border-right: 1px solid #e2e6e3; background: #fff; color: #285b9a; font-size: 16px; font-weight: 800; cursor: pointer; }
.zoom-btn:last-child { border-right: 0; }
.zoom-btn:hover { background: #f2f7f5; }
.zoom-fit { font-size: 12px; }
.drag-hint { margin: 0 0 8px; color: #65736e; font-size: 12px; }
.action-message { margin: 0 0 12px; padding: 9px 12px; border: 1px solid #b8ddce; border-radius: 8px; background: #f1faf6; color: #1f5c4b; font-size: 13px; font-weight: 700; }
.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }
.canvas-fullscreen { width: 100%; background: #071521; border-radius: 12px; }
.canvas-fullscreen:fullscreen { width: 100vw; height: 100vh; border-radius: 0; background: #071521; }
.floor-canvas { width: 100%; height: 66vh; min-height: 420px; border: 1px solid #20394a; border-radius: 12px; background: #071521; overflow: hidden; }
.canvas-fullscreen:fullscreen .floor-canvas { width: 100vw; height: 100vh; min-height: 100vh; border: 0; border-radius: 0; }

.offcanvas-backdrop { position: fixed; z-index: 60; inset: 0; background: rgb(15 30 26 / 35%); }
.offcanvas { position: fixed; z-index: 61; top: 0; right: 0; display: flex; width: min(480px, 100vw); height: 100vh; flex-direction: column; background: #fff; box-shadow: -12px 0 40px rgb(15 30 26 / 20%); }
.offcanvas-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px; border-bottom: 1px solid #e2e6e3; }
.offcanvas-head h3 { margin: 0; color: #1a2b26; font-size: 20px; }
.offcanvas-head p:last-child { margin: 4px 0 0; color: #65736e; font-size: 13px; }
.close-btn { border: 0; background: transparent; color: #53625d; font-size: 28px; line-height: 1; cursor: pointer; }
.offcanvas-body { overflow-y: auto; padding: 20px; }
.batch-offcanvas { width: min(900px, 100vw); }
.batch-form { display: flex; flex-direction: column; gap: 18px; }
.batch-section { padding-bottom: 18px; border-bottom: 1px solid #e5ece9; }
.batch-section h4 { margin: 0 0 12px; color: #233b33; }
.batch-section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.batch-section-head span { color: #65736e; font-size: 12px; font-weight: 700; }
.machine-choice-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(105px, 1fr)); gap: 8px; }
.machine-choice { display: flex; min-height: 58px; flex-direction: column; justify-content: center; padding: 8px 10px; border: 1px solid #cdd8d4; border-radius: 8px; background: #f7faf9; cursor: pointer; }
.machine-choice input { position: absolute; opacity: 0; pointer-events: none; }
.machine-choice strong { color: #20372f; font-size: 13px; }
.machine-choice small { color: #687a73; font-size: 10px; text-transform: capitalize; }
.machine-choice.selected { border-color: #16869a; background: #daf1f4; box-shadow: inset 0 0 0 1px #16869a; }
.machine-choice.unavailable { opacity: .48; cursor: not-allowed; }
.matrix-scroll { overflow-x: auto; border: 1px solid #dce5e1; border-radius: 9px; }
.assignment-matrix { width: 100%; min-width: 620px; border-collapse: collapse; font-size: 12px; }
.assignment-matrix th, .assignment-matrix td { padding: 8px; border-right: 1px solid #e3e9e6; border-bottom: 1px solid #e3e9e6; text-align: center; }
.assignment-matrix thead th { position: sticky; top: 0; background: #f1f6f4; color: #334a42; }
.assignment-matrix tbody th { position: sticky; left: 0; background: #fff; color: #223a32; text-align: left; }
.matrix-radio { display: grid; width: 30px; height: 30px; margin: auto; place-items: center; border: 1px solid #bfcac6; border-radius: 7px; color: transparent; cursor: pointer; }
.matrix-radio input { position: absolute; opacity: 0; pointer-events: none; }
.matrix-radio.selected { border-color: #16869a; background: #16869a; color: #fff; }
.matrix-quantity { width: 88px; padding: 6px; border: 1px solid #cbd6d2; border-radius: 6px; }
.batch-actions { position: sticky; bottom: -20px; display: flex; justify-content: flex-end; gap: 8px; margin: 0 -20px -20px; padding: 14px 20px; border-top: 1px solid #dfe7e4; background: #fff; }
.panel-state { padding: 30px 0; text-align: center; color: #65736e; }
.panel-state.error, .panel-error { color: #b33a3a; }
.panel-error { padding: 9px 12px; border-radius: 8px; background: #fff1f0; font-size: 13px; }
.detail-section { padding: 16px 0; border-bottom: 1px solid #eef2f0; }
.detail-section h4, .load-order-form h4 { margin: 0 0 12px; color: #1a2b26; font-size: 14px; }
.state-line { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.state-dot { width: 14px; height: 14px; border: 1px solid #9fb3aa; border-radius: 50%; }
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 0; }
.detail-grid div { min-width: 0; }
.detail-grid dt { margin-bottom: 3px; color: #71807a; font-size: 11px; text-transform: uppercase; }
.detail-grid dd { margin: 0; overflow-wrap: anywhere; color: #1a2b26; font-size: 13px; font-weight: 700; }
.empty-inline, .panel-hint { color: #65736e; font-size: 13px; }
.panel-actions { display: flex; gap: 8px; padding-top: 18px; }
.panel-actions button { flex: 1; }
.btn-danger { padding: 8px 16px; border: 0; border-radius: 8px; background: #b33a3a; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; }
.btn-danger:disabled { opacity: .55; cursor: not-allowed; }
.load-order-form { display: grid; gap: 12px; margin-top: 18px; padding: 16px; border: 1px solid #dfe7e3; border-radius: 10px; background: #f8fbfa; }
.load-order-form label { display: grid; gap: 6px; color: #53625d; font-size: 12px; font-weight: 700; }
.load-order-form input, .load-order-form select { min-width: 0; padding: 9px 10px; border: 1px solid #cbd7d2; border-radius: 8px; background: #fff; font: inherit; }

.btn-primary, .btn-secondary { padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; }
.btn-primary { border: none; background: #147a8a; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #0f6573; }
.btn-primary:disabled { opacity: .55; cursor: not-allowed; }
.btn-secondary { border: 1px solid #cbd7d2; background: #fff; color: #285b9a; }
.state-message .btn-primary { margin-top: 12px; }

.modal-overlay { position: fixed; z-index: 50; inset: 0; display: grid; padding: 20px; background: rgb(15 30 26 / 45%); place-items: center; }
.modal { display: grid; width: min(460px, 100%); gap: 12px; padding: 20px; border-radius: 12px; background: #fff; box-shadow: 0 20px 60px rgb(15 30 26 / 30%); }
.modal h3 { margin: 0; font-size: 16px; color: #1a2b26; }
.modal label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
.modal input, .modal select { padding: 9px 10px; border: 1px solid #cbd7d2; border-radius: 8px; font: inherit; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.modal .error { color: #b33a3a; font-size: 12px; }
</style>
